import type { FundLike } from '@tahi/core';
import { Chip } from '../../../ui';
import { copy } from '../copy';
import { LABEL } from '../styles';

type FundRef = Pick<FundLike, 'id' | 'name'>;

export interface FundPickerProps {
  funds: readonly FundRef[];
  value: string | null;
  onChange(id: string | null): void;
  /** Shown while nothing is picked. */
  hint?: string;
}

export function FundPicker({ funds, value, onChange, hint }: FundPickerProps) {
  return (
    <div className="flex flex-col gap-2">
      <span aria-hidden="true" className={LABEL}>
        {copy.fundLabel}
      </span>
      <div role="group" aria-label={copy.fundLabel} className="flex flex-wrap gap-2">
        {funds.map((f) => (
          <Chip
            key={f.id}
            selected={f.id === value}
            aria-pressed={f.id === value}
            onClick={() => onChange(f.id === value ? null : f.id)}
          >
            {f.name}
          </Chip>
        ))}
      </div>
      {hint && value === null ? <p className="text-sm text-muted">{hint}</p> : null}
    </div>
  );
}

/** What happens to an expense with no fund picked (spec §6 auto-deduct). */
export function fundHint(
  settings: { autoDeduct: boolean; defaultFundId: string | null } | undefined,
  funds: readonly FundRef[],
): string {
  const fallback = settings?.autoDeduct
    ? funds.find((f) => f.id === settings.defaultFundId)
    : undefined;
  return fallback ? copy.fundHintDefault(fallback.name) : copy.fundHintNone;
}
